import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";

import type {
  SpecLinePerPlanBrowserInput,
  SpecSelectionManufacturerBrowserInput,
} from "@/api/contracts/spec-editor";
import {
  specLineUpdatePerPlan,
  specSelectionUpdateManufacturer,
} from "@/api/functions/spec-editor";
import { useLookupEditing } from "@/hooks/catalog-admin";
import {
  editorMutationOptions,
  patchLine,
  patchManufacturer,
  type EditorCacheTarget,
  type EditorGuess,
} from "@/hooks/spec-editor-cache";
import { specificationLinesQueryKeyPrefix } from "@/hooks/specifications";
import type { LookupOption } from "@/lib/catalog-admin";

// The Editor's per-plan verbs (#803): what #810's `Set as Per Plan` checkbox
// and #858's manufacturer dialog call. Each runs through #797's harness
// (`editorMutationOptions`) with its own `ERRORS` key, and its result — the
// line whole, its selections with their per-plan manufacturer — lands through
// the one cache updater (ADR-0014 decisions 1–6).
//
// **Per plan guesses** (the selections rulings item 11): the box ticks at once,
// rolled back exactly on failure, and overwritten by the result on success. The
// manufacturer waits for the server, as the source's did; its failure surfaces
// Atlas's own string (`UPDATE_SELECTION_MANUFACTURER`, a named parity break —
// the source only logged it).
//
// No hook touches the Active selection — the edit page owns it. **The source's
// re-emit is the menu's** (`LineMoreActions/actions.ts:448-458`, `:469-476`:
// the line with its new flag and the selection with `itemId: null`, both put
// back on failure). The cache's guess carries the flag, but it keeps the
// selection's item, so it cannot carry the `itemId: null`: #810's `Set as Per
// Plan` sets the row with its item cleared at the click, and puts the plain row
// back if the write fails while that set lasts (#861 D3 a, `spec-line-menu`).

/** The per-plan box's guess: the held line's flag, set — never toggled — its
 *  selections and their items kept behind the placeholder. */
export const updateSpecLinePerPlanGuess: EditorGuess<SpecLinePerPlanBrowserInput> = (
  data,
  { lineId, isPerPlan },
) => patchLine(data, lineId, (line) => ({ ...line, perPlan: isPerPlan }));

/** Set as Per Plan — optimistic; the response wins. */
export function updateSpecLinePerPlanMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLinePerPlanBrowserInput>(queryClient, {
    mutationFn: (data) => specLineUpdatePerPlan({ data }),
    error: "UPDATE_LINE_IS_PER_PLAN",
    guess: updateSpecLinePerPlanGuess,
  });
}

export function useUpdateSpecLinePerPlan() {
  return useMutation(updateSpecLinePerPlanMutationOptions(useQueryClient()));
}

/** The per-plan selection's manufacturer — set, or cleared with `null`; lands
 *  when the server answers. */
export function updateSpecSelectionManufacturerMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecSelectionManufacturerBrowserInput>(queryClient, {
    mutationFn: (data) => specSelectionUpdateManufacturer({ data }),
    error: "UPDATE_SELECTION_MANUFACTURER",
  });
}

export function useUpdateSpecSelectionManufacturer() {
  return useMutation(updateSpecSelectionManufacturerMutationOptions(useQueryClient()));
}

/**
 * A manufacturer the per-plan dialog renamed inline, laid onto every held lines
 * view of the version — the base view and each search (#858). The source's
 * rename wrote the row into the shared manufacturer cache every editor row
 * reads, so each row showing it renamed at once with no reload
 * (`LinePerPlan/actions.ts:124-145`); here the rows carry the name, so it is
 * `patchManufacturer` over each view. Other specs' views are not touched: they
 * read fresh on their next load.
 */
/** The version a rename reaches: its spec and its id, whatever the scope. */
export type EditorVersion = Pick<EditorCacheTarget, "specificationId" | "versionId">;

export function landSpecManufacturerRename(
  queryClient: QueryClient,
  { specificationId, versionId }: EditorVersion,
  renamed: LookupOption,
): void {
  queryClient.setQueriesData<Parameters<typeof patchManufacturer>[0]>(
    { queryKey: [...specificationLinesQueryKeyPrefix, specificationId, versionId] },
    (data) => data && patchManufacturer(data, renamed.id, renamed.name),
  );
}

/** The catalog's inline manufacturer rename, then its reach onto this
 *  version's rows — the per-plan dialog's `onRenameOption`. */
export function useRenameSpecManufacturer(version: EditorVersion) {
  const queryClient = useQueryClient();
  const { renameOption } = useLookupEditing();
  const rename = renameOption("manufacturer");
  return async (id: number, name: string): Promise<LookupOption> => {
    const renamed = await rename(id, name);
    landSpecManufacturerRename(queryClient, version, renamed);
    return renamed;
  };
}
